import React, { useEffect } from 'react'
import { cn } from '../../../../lib/utils'
import '../../../../lib/type-fixes'

interface ScaleQuestionProps {
  question: {
    scale_min: number
    scale_max: number
    scale_label_min: string
    scale_label_max: string
    question_text?: string
  }
  value: number | undefined
  onChange: (value: number) => void
}

function ScaleQuestion({ question, value, onChange }: ScaleQuestionProps) {
  // Function to clear hover states manually
  const clearHoverStates = () => {
    document.querySelectorAll('[data-scale-button]').forEach(button => {
      button.classList.remove('hover:bg-primary/20', 'hover:border-primary/50')
      button.blur() // Ensures focus is removed
    })
  }

  // Clear hover states when value changes or component unmounts
  useEffect(() => {
    clearHoverStates()
    return clearHoverStates
  }, [value])

  // Handle change with hover fix
  const handleChange = (newValue: number, event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur() // Force blur to remove focus styles
    clearHoverStates() // Manually remove hover states
    onChange(newValue)
  }

  return (
    <div className="w-full max-w-lg mx-auto space-y-6">
      <div className="flex flex-wrap justify-center gap-2">
        {Array.from(
          { length: question.scale_max - question.scale_min + 1 }, 
          (_, i) => i + question.scale_min
        ).map((scaleValue) => (
          <button
            key={scaleValue}
            data-scale-button
            onClick={(e) => handleChange(scaleValue, e)}
            className={cn(
              "h-7 w-7 p-0 sm:h-10 sm:w-10 md:h-13 md:w-13",
              "text-xs sm:text-sm md:text-base rounded-md",
              "transition-colors relative",
              "touch-none select-none",
              value === scaleValue ? [
                "bg-primary text-primary-foreground shadow-md",
                "md:hover:bg-primary/90 md:active:bg-primary/80",
                "dark:bg-primary dark:text-primary-foreground",
                "dark:md:hover:bg-primary/90 dark:md:active:bg-primary/80"
              ] : [
                "bg-background text-foreground border border-border",
                "md:hover:bg-primary/20 md:hover:border-primary/50",
                "md:active:bg-primary/30",
                "dark:bg-background dark:text-foreground dark:border-border/80",
                "dark:md:hover:bg-primary/20 dark:md:hover:border-primary/50",
                "dark:md:active:bg-primary/30"
              ]
            )}
          >
            {scaleValue}
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-4 sm:gap-20 md:gap-40 text-xs md:text-sm text-muted-foreground">
        <div>{`${question.scale_label_min} (${question.scale_min})`}</div>
        <div>{`${question.scale_label_max} (${question.scale_max})`}</div>
      </div>
    </div>
  )
}

export { ScaleQuestion }